import { useSyncExternalStore } from "react";
import { FormattedDate, FormattedMessage } from "react-intl";
import { ClockIcon } from "@/icons";
import { RelativeTime } from "@/components/RelativeTime";
import { useIsPast } from "@/utils/hooks";
import { cn } from "@/utils/tailwind";

const subscribe = () => () => {};

/// False during SSR and hydration, true afterwards.
const useIsClient = () =>
  useSyncExternalStore(
    subscribe,
    () => true,
    () => false,
  );

interface SubmissionDeadlineProps {
  deadline: string;
  className?: string;
}

/// When submissions close, in the viewer's own timezone.
export function SubmissionDeadline({
  deadline,
  className,
}: Readonly<SubmissionDeadlineProps>) {
  const isClosed = useIsPast(deadline);
  // The server doesn't know the viewer's timezone, so it only renders the
  // relative time.
  const isClient = useIsClient();
  const date = new Date(deadline);
  const relative = <RelativeTime date={date} />;
  return (
    <p
      className={cn(
        "flex flex-wrap items-center gap-x-1.5 text-sm text-grey-600",
        className,
      )}
    >
      <ClockIcon className="size-4 shrink-0" />
      <span>
        {isClosed ? (
          <FormattedMessage
            defaultMessage="Submissions closed {relative}"
            values={{ relative }}
          />
        ) : (
          <FormattedMessage
            defaultMessage="Submissions close {relative}"
            values={{ relative }}
          />
        )}
      </span>
      {isClient && (
        <span className="text-grey-500">
          (
          <FormattedDate
            value={date}
            year="numeric"
            month="short"
            day="numeric"
            hour="numeric"
            minute="2-digit"
            timeZoneName="short"
          />
          )
        </span>
      )}
    </p>
  );
}
